import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  Divider,
  FormControlLabel,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const CustomerList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(true);

  // Top cards data
  const topCardsData = [
    {
      id: 1,
      value: "12",
      label: "Total Customers",
      icon: "stash:user-id",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 2,
      value: "18",
      label: "Total Jobsites",
      icon: "proicons:location",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 3,
      value: "8",
      label: "Synced",
      icon: "material-symbols:sync-rounded",
      iconColor: "#DD992F",
      bgColor: "#FFF3E0",
    },
    {
      id: 4,
      value: "2",
      label: "Total Errors",
      icon: "bx:error",
     iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Customer data
  const customers = [
    {
      id: 1,
      name: "ABC Construction Corp",
      qbName: "ABC Construction Corp",
      lastSync: "2 hours ago",
      status: "synced",
      type: "Parent",
      jobsites: [
        {
          name: "Downtown Office Building",
          address: "123 Main St, City, ST 12345"
        },
        {
          name: "Residential Complex A", 
          address: "456 Oak Ave, City, ST 12345"
        }
      ]
    },
    {
      id: 2,
      name: "Smith Residential Services",
      qbName: "Smith Residential Services", 
      lastSync: "1 hour ago",
      status: "synced",
      type: "Parent",
      jobsites: [
        {
          name: "Main Office",
          address: "780 Pine St, City, ST 12345"
        }
      ]
    },
    {
      id: 3,
      name: "Green Valley Apartments",
      qbName: "Not mapped",
      lastSync: "Never",
      status: "pending",
      secondaryStatus: "new",
      type: "Parent",
      jobsites: [
        {
          name: "Building A",
          address: "100 Green Valley Dr, City, ST 12345"
        },
        {
          name: "Building B",
          address: "102 Green Valley Dr, City, ST 12345"
        }
      ]
    },
    {
      id: 4,
      name: "Metro Property Management",
      qbName: "Metro Property Mgmt",
      lastSync: "Failed 3 hours ago",
      status: "error",
      secondaryStatus: "conflict",
      type: "Parent",
      jobsites: [
        {
          name: "Corporate HQ",
          address: "555 Business Blvd, City, ST 12345"
        }
      ]
    }
  ];

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Customers and Jobsites"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Main Card */}
        <Card sx={{ overflow: "hidden" }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                <Typography variant="body1">Sync Enabled</Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={syncEnabled}
                      onChange={(e) => setSyncEnabled(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                </Box>
                
                <Button
                  variant="contained"
                  color="main"
                  size="medium"
                  startIcon={
                    <Icon icon="heroicons:arrow-path" width={20} height={20} />
                  }
                  disabled={!syncEnabled}
                >
                  Sync Now
                </Button>
              </Box>
            </Box>

            {/* Top Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 140,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Card 
              sx={{ 
                borderRadius: 2, 
                border: "1px solid", 
                borderColor: "divider",
                backgroundColor: "background.paper",
                p: 3,
                mb: 4
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Customer List
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Cleri customers sync to QuickBooks as Parent/Sub-Customer relationship based on jobsites
                  </Typography>
                </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CustomTextField
                    placeholder="Search"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ 
                      width: 200,
                      '& .MuiOutlinedInput-root': {
                        height: '36px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon icon="tabler:search" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {customers.map((customer) => (
                  <Card
                    key={customer.id}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      overflow: "visible",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Customer Header */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: 650, color: "text.primary",mr: 5 }}>
                                {customer.name}
                              </Typography>
                              
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2,ml: 5 }}>
                                <Chip
                                  label="Parent"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    color: "#525866",
                                    borderColor: "#525866",
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    fontSize: "0.78rem",
                                    height: 28,
                                    px: 1.5,
                                  }}
                                />
                                {customer.secondaryStatus && (
                                  <Chip
                                    label={customer.secondaryStatus === "new" ? "New" : "Conflict"}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 1,
                                      backgroundColor: customer.secondaryStatus === "new" ? "#003A701A" : "#DA12121A",
                                      color: customer.secondaryStatus === "new" ? "#003A70" : "#DA1212",
                                      borderColor: customer.secondaryStatus === "new" ? "#003A701A" : "#DA12121A",
                                      fontWeight: 600,
                                      fontSize: "0.78rem",
                                      height: 28,
                                      px: 1.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 ,mb:3}}>
                              <Typography variant="body1">
                                QB Name: {customer.qbName}
                              </Typography>
                              <Typography variant="body1">
                                Last Sync: {customer.lastSync}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={customer.status === "synced" ? "Synced" : customer.status === "pending" ? "Pending" : "Error"}
                            size="small"
                            sx={{
                              backgroundColor: 
                                customer.status === "synced" ? "#E8F5E9" : 
                                customer.status === "pending" ? "#FFF3E0" : "#FFEBEE",
                              color: 
                                customer.status === "synced" ? "#23A26D" : 
                                customer.status === "pending" ? "#DD992F" : "#D32F2F",
                              fontWeight: 620,
                              fontSize: "0.78rem",
                              height: 24,
                              px: 2.5,
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Jobsites Section */}
                      <Box>
                        {/* Jobsites List with Single Vertical Line */}
                        <Box sx={{ 
                          position: "relative",
                          pl: 3, // Add left padding to make space for the line
                        }}>
                          {/* Single Vertical Line covering all jobsites */}
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: "1.2px",
                              backgroundColor: "divider",
                            }}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                            <Icon 
                              icon="proicons:location" 
                              width={18} 
                              height={18} 
                            />
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 600,
                                color: "text.primary"
                              }}
                            >
                              Jobsites ({customer.jobsites.length})
                            </Typography>
                          </Box>
                          
                          {/* Jobsites Content */}
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {customer.jobsites.map((jobsite, index) => (
                              <Box 
                                key={index}
                                sx={{ 
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                {/* Jobsite Info */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      fontWeight: 600, 
                                      mb: 0.5,
                                      color: "text.primary"
                                    }}
                                  >
                                    {jobsite.name}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                  >
                                    {jobsite.address}
                                  </Typography>
                                </Box>

                                {/* Sub Customer Button */}
                                <Button 
                                  variant="outlined" 
                                  color="primary"
                                  size="small"
                                  sx={{ 
                                    minWidth: "120px",
                                    whiteSpace: "nowrap",
                                    ml: 2
                                  }}
                                >
                                  Sub Customer
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Card>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default CustomerList;